import {
  Book,
  Cloud,
  ReaderState,
  ErrorType,
  NewReaderProps,
} from '@readcloud/data';
import { NewReader } from '@readcloud/new-reader';
import {
  annotationsActions,
  appStatesActions,
  appStatesSelectors,
  applicationActions,
  applicationSelectors,
  authSelectors,
  bookSelectors,
  bookmarksActions,
  cloudSelectors,
  readerActions,
  reportActions,
  useAppDispatch,
} from '@readcloud/state';
import React, { useEffect } from 'react';
import { getLatestRcsResources, rewriteResourceURL } from '@readcloud/common';

import { useSelector } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { usePrepareDocument } from '../hooks/usePrepareDocument';
import { useReaderState } from '../hooks/useReaderState';
import { usePageContext } from './Page';
import { useHasWindowFocus } from '@readcloud/common';

export function ReaderPage() {
  const clouds = useSelector(cloudSelectors.getClouds);
  const books = useSelector(bookSelectors.getBooks);
  const appStates = useSelector(appStatesSelectors.getAppStates);
  const user = useSelector(authSelectors.getUser);
  const appState = useSelector(applicationSelectors.getApplcationState);
  const config = useSelector(applicationSelectors.getAppConfig);
  const history = useHistory();
  const location = useLocation();
  const pageContext = usePageContext();
  const dispatch = useAppDispatch();
  const params = useParams<{
    bookId: string;
    cloudId: string;
    pageNumber?: string;
  }>();

  // Read initial page/location from URL query parameter (?p=...)
  const { initialPage, initialLocation } = React.useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get('p');
    if (pageParam) {
      // Check if it's an EPUB CFI (starts with "epubcfi")
      if (pageParam.startsWith('epubcfi')) {
        return { initialPage: undefined, initialLocation: pageParam };
      }
      // Try to parse as number for PDF page
      const pageNum = parseInt(pageParam, 10);
      if (!isNaN(pageNum)) {
        return { initialPage: pageNum, initialLocation: undefined };
      }
      // Fallback: treat as location string (could be other format)
      return { initialPage: undefined, initialLocation: pageParam };
    }
    return { initialPage: undefined, initialLocation: undefined };
  }, [location.search]);
  const [book, setBook] = React.useState<Book>();
  const [cloud, setCloud] = React.useState<Cloud>();
  const [readerData, setReaderData] = React.useState<
    ReaderState['reader'] | undefined
  >();
  const [appStatesInitialized, setAppStatesInitialized] = React.useState(false);
  const [bookAndCloudInitialized, setBookAndCloudInitialized] =
    React.useState(false);

  const deltaIntervalHandle = React.useRef(undefined);

  // Step 1: Initialize app states
  React.useEffect(() => {
    dispatch(appStatesActions.asyncActions.getAppStates({})).then(() => {
      setAppStatesInitialized(true);
    });
  }, []);

  // Step 2: Initialize book and cloud
  React.useEffect(() => {
    if (
      params.bookId &&
      params.cloudId &&
      books?.length &&
      Object.values(clouds || {})?.length
    ) {
      const tempBook = books.find((b) => b.id === params.bookId) || undefined;
      const tempCloud = clouds[params.cloudId] || undefined;
      setBook(tempBook);
      setCloud(tempCloud);
      setBookAndCloudInitialized(true);
    }
  }, [clouds, books, params]);

  // Step 3: Initialize reader data once we have book and cloud. Still gated
  // on appStates because viewState (last-read page/location) comes from there.
  React.useEffect(() => {
    if (bookAndCloudInitialized && appStatesInitialized && book && cloud) {
      const { reader } = useReaderState(book, cloud, appStates, user, initialPage, initialLocation);
      setReaderData(reader);
    }
  }, [bookAndCloudInitialized, appStatesInitialized, book, cloud, initialPage, initialLocation]);

  // Download params derive from `book` alone and do not depend on appStates,
  // so we can start the download before the appStates fetch completes.
  const downloadParams = React.useMemo(() => {
    if (!book) return undefined;
    const latest = book.rcsData ? getLatestRcsResources(book.rcsData) : undefined;
    const fileUrl =
      (latest && latest.bookbytesUrl) ||
      (book.downloadURI && rewriteResourceURL(book.downloadURI));
    return {
      fileUrl,
      isbn: book.isbn || undefined,
      longId: latest?.bookbytes?.longId,
      minor: latest?.bookbytes?.minor,
    };
  }, [book]);

  // Step 4: Prepare document hook — driven by downloadParams so it's ready
  // as soon as the book record is available.
  const {
    error,
    data,
    type,
    progress,
    loading,
    prepareDocument,
    cancelPrepare,
  } = usePrepareDocument({
    minor: downloadParams?.minor,
    longId: downloadParams?.longId,
    isbn: downloadParams?.isbn,
    onBeforePrepare: undefined,
  });

  // Step 5: Kick off the download the moment book + fileUrl are resolved.
  // Only fire once per bookId — background /get/all refreshes can mint a new
  // pre-signed URL for the same book, and we don't want that to reset the
  // reader's loading state and re-download bytes we already have in memory.
  // Page/location restore (which needs appStates) happens independently in
  // Step 3 and will apply once the document has loaded.
  const preparedBookIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (
      bookAndCloudInitialized &&
      book?.id &&
      downloadParams?.fileUrl &&
      preparedBookIdRef.current !== book.id
    ) {
      preparedBookIdRef.current = book.id;
      prepareDocument && prepareDocument(downloadParams.fileUrl);
    }
  }, [prepareDocument, bookAndCloudInitialized, book?.id, downloadParams?.fileUrl]);

  const onReload = React.useCallback(() => {
    if (downloadParams?.fileUrl) {
      prepareDocument && prepareDocument(downloadParams.fileUrl);
    }
  }, [prepareDocument, downloadParams?.fileUrl]);

  React.useEffect(() => {
    const userPrefs = appStates.find(
      (appState) => appState?.tags?.realm === 'UserPrefState'
    );

    if (userPrefs?.state) {
      dispatch(
        applicationActions.updateApplication({ userPrefs: userPrefs?.state })
      );
    }
  }, [appStates]);

  React.useEffect(() => {
    if (readerData?.bookId) {
      // Only dispatch book metadata — the PDF/EPUB loader owns numPages,
      // pageLabels, outline, loadingPageLabels, locationIndex*, and we
      // mustn't clobber them from here. On a cached-book open the loader
      // finishes before appStates arrives, so spreading the whole readerData
      // would reset the nav bar back into its loading state.
      dispatch(
        readerActions.updateReaderData({
          bookId: readerData.bookId,
          cloudId: readerData.cloudId,
          title: readerData.title,
          fileUrl: readerData.fileUrl,
          isbn: readerData.isbn,
          longId: readerData.longId,
          minor: readerData.minor,
          rcsData: readerData.rcsData,
          bookOptions: readerData.bookOptions,
          printRights: readerData.printRights,
          version: readerData.version,
        })
      );
      dispatch(readerActions.updateLocalViewState(readerData.viewState));
    }
  }, [readerData?.bookId]);

  React.useEffect(() => {
    if (book?.id) {
      dispatch(
        annotationsActions.asyncActions.getAnnotations({
          filterBookIds: [book.id],
        })
      );
    }
  }, [book?.id]);

  React.useEffect(() => {
    if (book?.id) {
      dispatch(
        bookmarksActions.asyncActions.getBookmarks({ filterBookIds: [book.id] })
      );
    }
  }, [book?.id]);

  const windowIsVisible = useHasWindowFocus((focussed) => {
    if (focussed) {
      dispatch(annotationsActions.asyncActions.getDeltaAnnotations({}));
    }
  });

  React.useEffect(() => {
    let cancelled = false;
    if (
      config?.deltaPollingRealtimeIntervalSec &&
      appState.lastPoll.annotationsServerMillisec &&
      windowIsVisible
    ) {
      clearInterval(deltaIntervalHandle.current);
      deltaIntervalHandle.current = setInterval(() => {
        if (cancelled) {
          return;
        }
        dispatch(annotationsActions.asyncActions.getDeltaAnnotations({}));
      }, config?.deltaPollingRealtimeIntervalSec * 1000 || 30000);
    }
    return () => {
      cancelled = true;
      clearInterval(deltaIntervalHandle.current);
    };
  }, [
    config?.deltaPollingRealtimeIntervalSec,
    appState.lastPoll.annotationsServerMillisec,
    windowIsVisible,
  ]);

  useEffect(() => {
    if (book) {
      pageContext.setPageSubtitles(book.title);
    } else {
      pageContext.setPageSubtitles();
    }
  }, [book]);

  const documentData: NewReaderProps['documentData'] = React.useMemo(
    () => ({
      error: error,
      data: data || null,
      type: type || '',
      progress,
      loading: loading,
    }),
    [data, type, progress, loading]
  );

  return (
    <NewReader
      onGoBack={() => {
        cancelPrepare?.();
        history.push('/');
      }}
      onReload={onReload}
      documentData={documentData}
      className="rc-h-full rc-w-full"
    />
  );
}

export default ReaderPage;
